/**
 * ContactForm.jsx — Add / Edit contact.
 * All reads and writes go through the backend API.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createContact, updateContact, offboardContact, createFirm, lookupFirmByDomain, createTrial } from '../api/backend.js';

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com','googlemail.com','outlook.com','hotmail.com','live.com',
  'yahoo.com','icloud.com','me.com','mac.com',
  'qq.com','163.com','126.com','protonmail.com','proton.me',
]);

const FUND_TYPE_OPTIONS = ['Hedge Fund', 'Mutual Fund', 'Family Office', 'Pension', 'Endowment', 'Sovereign Wealth', 'VC / PE'];
const SOURCE_OPTIONS    = ['BigDough', 'Referral', 'Conference', 'LinkedIn', 'Cold Email', 'Website', 'Partner', 'Calendar Sync', 'Gmail Sync'];
const ROLE_OPTIONS      = ['Portfolio Manager', 'Analyst', 'Trader', 'COO', 'CFO', 'CTO', 'IR / Marketing', 'Operations'];
const STRATEGY_OPTIONS  = ['Long/Short Equity', 'Global Macro', 'Fixed Income', 'Quantitative', 'Multi-Strategy', 'Event Driven', 'Credit'];
const AUM_TIER_OPTIONS  = ['<$100M', '$100M–$500M', '$500M–$1B', '$1B–$5B', '$5B+'];

function FirmCombobox({ firms, value, onChange }) {
  const selectedFirm = firms.find((f) => f.firm_id === value) ?? null;
  const [query, setQuery]   = useState('');
  const [open, setOpen]     = useState(false);
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const filtered = query.trim() === ''
    ? firms
    : firms.filter((f) => f.firm_name.toLowerCase().includes(query.trim().toLowerCase()));

  function select(firm) {
    onChange(firm ? firm.firm_id : '');
    setQuery('');
    setOpen(false);
  }

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 4,
    border: `1px solid ${focused ? 'var(--c-primary)' : 'var(--c-border)'}`,
    fontSize: 13.5, background: '#fff', boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder={selectedFirm ? selectedFirm.firm_name : '— No firm —'}
        value={open ? query : (selectedFirm ? selectedFirm.firm_name : '')}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setFocused(true); setOpen(true); }}
        onBlur={() => setFocused(false)}
        style={{ ...inputStyle, color: !open && !selectedFirm ? 'var(--c-text-3)' : 'var(--c-text)' }}
        autoComplete="off"
      />
      {selectedFirm && !open && (
        <button
          type="button"
          onClick={() => select(null)}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-3)', fontSize: 16, lineHeight: 1, padding: 0 }}
          title="Clear firm"
        >×</button>
      )}
      {open && (
        <div style={{
          position: 'absolute', zIndex: 100, top: '100%', left: 0, right: 0,
          background: '#fff', border: '1px solid var(--c-border)', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2,
        }}>
          <div
            onMouseDown={() => select(null)}
            style={{ padding: '7px 10px', fontSize: 13.5, color: 'var(--c-text-3)', cursor: 'pointer' }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={(e) => e.currentTarget.style.background = ''}
          >— No firm —</div>
          {filtered.length === 0 && (
            <div style={{ padding: '7px 10px', fontSize: 13, color: 'var(--c-text-3)' }}>No matches</div>
          )}
          {filtered.map((f) => (
            <div
              key={f.firm_id}
              onMouseDown={() => select(f)}
              style={{
                padding: '7px 10px', fontSize: 13.5, cursor: 'pointer',
                background: f.firm_id === value ? '#eff6ff' : '',
                fontWeight: f.firm_id === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (f.firm_id !== value) e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { if (f.firm_id !== value) e.currentTarget.style.background = ''; }}
            >{f.firm_name}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function OtherSelect({ id, value, onChange, options, placeholder }) {
  // otherMode covers the gap where "Other…" is chosen but the custom text is
  // still empty — deriving purely from `value` made an empty value look like
  // "nothing selected", so the text input never appeared.
  const [otherMode, setOtherMode] = useState(false);
  const isOther = otherMode || (value !== '' && !options.includes(value));
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <select
        id={id}
        value={isOther ? '__other__' : value}
        onChange={(e) => {
          if (e.target.value === '__other__') { setOtherMode(true); onChange(''); }
          else { setOtherMode(false); onChange(e.target.value); }
        }}
        style={{ flex: 1, padding: '7px 10px', borderRadius: 4, border: '1px solid var(--c-border)', fontSize: 13.5, background: '#fff', color: 'var(--c-text)' }}
      >
        <option value="">{placeholder ?? 'Select…'}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Other…</option>
      </select>
      {isOther && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter custom value"
          style={{ flex: 1, padding: '7px 10px', borderRadius: 4, border: '1px solid var(--c-primary)', fontSize: 13.5 }}
          autoFocus
        />
      )}
    </div>
  );
}

function Field({ label, required, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: 12.5, color: 'var(--c-text-2)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.3px' }}>
        {label}{required && <span style={{ color: 'var(--c-red)', marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', padding: '7px 10px', borderRadius: 4, border: '1px solid var(--c-border)', fontSize: 13.5 }}
    />
  );
}

function genId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function formatAum(val) {
  if (val == null || val === '') return null;
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString()}`;
}

export default function ContactForm({ contact, prefill = {}, data, getToken, onSaved, onCancel }) {
  const isEdit = !!contact;
  const { firms = [] } = data ?? {};

  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState(null);

  const [fields, setFields] = useState({
    name:   contact?.name   ?? prefill.name   ?? '',
    email:  contact?.email  ?? prefill.email  ?? '',
    role:   contact?.role   ?? contact?.title ?? prefill.role   ?? '',
    source: contact?.source ?? prefill.source ?? '',
    notes:  contact?.notes  ?? prefill.notes  ?? '',
  });

  // firm_id in prefill → pre-select existing firm (caller already resolved it, e.g. calendar domain match).
  // firm_name in prefill (no firm_id) → start in 'new' mode; domain lookup may upgrade to 'existing'.
  const prefillFirmId = contact?.firm_id ?? prefill.firm_id ?? '';
  const [firmMode, setFirmMode]             = useState(prefillFirmId ? 'existing' : (prefill.firm_name ? 'new' : 'existing'));
  const [selectedFirmId, setSelectedFirmId] = useState(prefillFirmId);
  const [newFirm, setNewFirm] = useState({
    firm_name: prefill.firm_name ?? '',
    fund_type: '',
    strategy:  '',
    aum_tier:  '',
    hq_city:   '',
    parent_firm_id: '', // pod shops: optional link to the parent firm (e.g. Millennium)
  });
  const [firmAdvancedOpen, setFirmAdvancedOpen] = useState(false);

  // BD metadata from prefill — read-only, passed through to createContact payload
  const bdMeta = {
    bd_contact_id:   prefill.bd_contact_id   ?? null,
    bd_mandate:      prefill.bd_mandate       ?? null,
    bd_aum_personal: prefill.bd_aum_personal  ?? null,
  };
  const hasBdMeta = bdMeta.bd_contact_id || bdMeta.bd_mandate || bdMeta.bd_aum_personal != null;

  // Domain-based firm suggestion (populated after email blur)
  const [domainSuggestion, setDomainSuggestion] = useState(null); // null | { firms: [], dismissed: bool }
  const lookupInFlight = useRef(false);

  const triggerDomainLookup = useCallback(async (email) => {
    if (isEdit || selectedFirmId || prefillFirmId) return;
    const domain = String(email ?? '').trim().toLowerCase().split('@')[1] ?? '';
    if (!domain || PERSONAL_EMAIL_DOMAINS.has(domain)) return;
    if (lookupInFlight.current) return;
    lookupInFlight.current = true;
    try {
      const result = await lookupFirmByDomain(getToken, domain);
      if (result?.firms?.length > 0) {
        setDomainSuggestion({ firms: result.firms, dismissed: false });
      }
    } catch (_) { /* silently ignore — domain lookup is best-effort */ }
    finally { lookupInFlight.current = false; }
  }, [isEdit, selectedFirmId, prefillFirmId, getToken]);

  function acceptDomainSuggestion(firm) {
    setSelectedFirmId(firm.firm_id);
    setFirmMode('existing');
    setDomainSuggestion((s) => s ? { ...s, dismissed: true } : s);
  }

  function dismissDomainSuggestion() {
    setDomainSuggestion((s) => s ? { ...s, dismissed: true } : s);
  }

  function setField(key, val) { setFields((f) => ({ ...f, [key]: val })); }
  function setNewFirmField(key, val) { setNewFirm((f) => ({ ...f, [key]: val })); }

  function validate() {
    if (!fields.name.trim())   return 'Name is required.';
    if (!fields.email.trim())  return 'Email is required.';
    if (!/\S+@\S+\.\S+/.test(fields.email.trim())) return 'Email is not valid.';
    if (!fields.source.trim()) return 'Source is required.';
    if (firmMode === 'new' && !newFirm.firm_name.trim()) return 'Firm name is required when creating a new firm.';
    return null;
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    let savedContact = null; // passed to onSaved so callers can optimistically render the change
    try {
      let firmId = selectedFirmId;

      if (firmMode === 'new') {
        firmId = genId('firm');
        await createFirm(getToken, {
          firm_id:   firmId,
          firm_name: newFirm.firm_name.trim(),
          fund_type: newFirm.fund_type,
          strategy:  newFirm.strategy,
          aum_tier:  newFirm.aum_tier,
          hq_city:   newFirm.hq_city,
          parent_firm_id: newFirm.parent_firm_id,
        });
      }

      if (isEdit) {
        await updateContact(getToken, contact.contact_id, {
          name:    fields.name.trim(),
          email:   fields.email.trim().toLowerCase(),
          firm_id: firmId,
          role:    fields.role,
          source:  fields.source,
          notes:   fields.notes,
        });
        savedContact = {
          contact_id: contact.contact_id,
          name:       fields.name.trim(),
          email:      fields.email.trim().toLowerCase(),
          firm_id:    firmId,
          role:       fields.role,
          source:     fields.source,
          notes:      fields.notes,
        };
      } else {
        const newContactId = genId('c');
        savedContact = {
          _isNew:     true,
          contact_id: newContactId,
          name:       fields.name.trim(),
          email:      fields.email.trim().toLowerCase(),
          firm_id:    firmId,
          role:       fields.role,
          source:     fields.source,
        };
        await createContact(getToken, {
          contact_id:      newContactId,
          name:            fields.name.trim(),
          email:           fields.email.trim().toLowerCase(),
          firm_id:         firmId,
          role:            fields.role,
          source:          fields.source,
          notes:           fields.notes,
          added_date:      new Date().toISOString().slice(0, 10),
          // BD metadata — only included when present
          ...(bdMeta.bd_contact_id   != null && { bd_contact_id:   bdMeta.bd_contact_id }),
          ...(bdMeta.bd_mandate      != null && { bd_mandate:      bdMeta.bd_mandate }),
          ...(bdMeta.bd_aum_personal != null && { bd_aum_personal: bdMeta.bd_aum_personal }),
        });

        if (prefill._pendingTrial) {
          await createTrial(getToken, {
            trial_id:   genId('t'),
            contact_id: newContactId,
            firm_id:    firmId || '',
            start_date: prefill._pendingTrial.start_date,
            end_date:   prefill._pendingTrial.end_date,
            status:     'Active',
            notes:      `[${prefill._pendingTrial.outreach_status}]`,
          });
          // Mark this pending email as handled so the banner clears on next render.
          try {
            const key = 'pending_trial_contacts_handled_v1';
            const existing = JSON.parse(localStorage.getItem(key) ?? '[]');
            if (!existing.includes(fields.email.trim().toLowerCase())) {
              localStorage.setItem(key, JSON.stringify([...existing, fields.email.trim().toLowerCase()]));
            }
          } catch (_) {}
        }
      }

      onSaved(savedContact);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  async function handleDelete() {
    const confirmed = window.confirm(
      `Delete "${contact.name ?? contact.email}"?\n\nThey will be removed from every page (Contacts, Customers, Trial Management), their trials deleted, and their product account locked. BD imports will not re-add them.`
    );
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      await offboardContact(getToken, contact.contact_id);
      onSaved();
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  }

  return (
    <div>
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <Field label="Full Name" required>
        <TextInput value={fields.name} onChange={(v) => setField('name', v)} placeholder="Jane Smith" />
      </Field>

      <Field label="Email" required>
        <input
          type="email"
          value={fields.email}
          onChange={(e) => setField('email', e.target.value)}
          onBlur={(e) => triggerDomainLookup(e.target.value)}
          placeholder="jane@example.com"
          style={{ width: '100%', padding: '7px 10px', borderRadius: 4, border: '1px solid var(--c-border)', fontSize: 13.5 }}
        />
      </Field>

      <Field label="Source" required>
        <OtherSelect value={fields.source} onChange={(v) => setField('source', v)} options={SOURCE_OPTIONS} placeholder="How did you meet?" />
      </Field>

      <Field label="Role / Title">
        <OtherSelect value={fields.role} onChange={(v) => setField('role', v)} options={ROLE_OPTIONS} placeholder="Select role…" />
      </Field>

      <Field label="Firm">
        {/* Domain-match suggestion banner */}
        {domainSuggestion && !domainSuggestion.dismissed && !selectedFirmId && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, padding: '9px 12px', marginBottom: 10, fontSize: 13 }}>
            {domainSuggestion.firms.length === 1 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  Looks like <strong>{domainSuggestion.firms[0].firm_name}</strong>
                  <span style={{ color: 'var(--c-text-2)', fontWeight: 400 }}> ({domainSuggestion.firms[0].contact_count} contact{domainSuggestion.firms[0].contact_count !== 1 ? 's' : ''})</span>
                </span>
                <button
                  type="button"
                  className="btn btn--sm btn--primary"
                  style={{ fontSize: 12 }}
                  onClick={() => acceptDomainSuggestion(domainSuggestion.firms[0])}
                >
                  Use this firm
                </button>
                <button type="button" className="btn btn--sm btn--ghost" style={{ fontSize: 12 }} onClick={dismissDomainSuggestion}>
                  Ignore
                </button>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 6, color: 'var(--c-text-2)' }}>This domain matches multiple firms:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {domainSuggestion.firms.map((f) => (
                    <div key={f.firm_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1 }}>
                        <strong>{f.firm_name}</strong>
                        <span style={{ color: 'var(--c-text-2)', fontWeight: 400 }}> · {f.contact_count} contact{f.contact_count !== 1 ? 's' : ''}</span>
                      </span>
                      <button type="button" className="btn btn--sm btn--primary" style={{ fontSize: 12 }} onClick={() => acceptDomainSuggestion(f)}>
                        Use
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn--sm btn--ghost" style={{ fontSize: 12, alignSelf: 'flex-start', marginTop: 2 }} onClick={dismissDomainSuggestion}>
                    Ignore all
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="radio" value="existing" checked={firmMode === 'existing'} onChange={() => setFirmMode('existing')} />
            Use existing firm
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginTop: 4 }}>
            <input type="radio" value="new" checked={firmMode === 'new'} onChange={() => setFirmMode('new')} />
            Create new firm
          </label>
        </div>

        {firmMode === 'existing' && (
          <FirmCombobox firms={firms} value={selectedFirmId} onChange={setSelectedFirmId} />
        )}

        {firmMode === 'new' && (
          <div style={{ background: '#f8fafc', border: '1px solid var(--c-border)', borderRadius: 8, padding: 14, marginTop: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 12, color: 'var(--c-text-2)' }}>NEW FIRM</div>
            <Field label="Firm Name" required>
              <TextInput value={newFirm.firm_name} onChange={(v) => setNewFirmField('firm_name', v)} placeholder="Acme Capital" />
            </Field>
            <Field label="Parent Firm (optional — for pods, e.g. Millennium)">
              <FirmCombobox
                firms={firms}
                value={newFirm.parent_firm_id}
                onChange={(id) => setNewFirmField('parent_firm_id', id)}
              />
            </Field>

            <button
              type="button"
              onClick={() => setFirmAdvancedOpen((v) => !v)}
              style={{ fontSize: 12, color: 'var(--c-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: firmAdvancedOpen ? 12 : 0 }}
            >
              {firmAdvancedOpen ? '▾ Hide details' : '▸ Add fund type, strategy, AUM… (optional)'}
            </button>

            {firmAdvancedOpen && (
              <div style={{ marginTop: 12 }}>
                <Field label="Fund Type">
                  <OtherSelect value={newFirm.fund_type} onChange={(v) => setNewFirmField('fund_type', v)} options={FUND_TYPE_OPTIONS} placeholder="Select fund type…" />
                </Field>
                <Field label="Strategy">
                  <OtherSelect value={newFirm.strategy} onChange={(v) => setNewFirmField('strategy', v)} options={STRATEGY_OPTIONS} placeholder="Select strategy…" />
                </Field>
                <Field label="AUM Tier">
                  <OtherSelect value={newFirm.aum_tier} onChange={(v) => setNewFirmField('aum_tier', v)} options={AUM_TIER_OPTIONS} placeholder="Select AUM tier…" />
                </Field>
                <Field label="HQ City">
                  <TextInput value={newFirm.hq_city} onChange={(v) => setNewFirmField('hq_city', v)} placeholder="New York" />
                </Field>
              </div>
            )}
          </div>
        )}
      </Field>

      <Field label="Notes">
        <textarea
          value={fields.notes}
          onChange={(e) => setField('notes', e.target.value)}
          placeholder="Any context, meeting notes, next steps…"
          rows={3}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 4, border: '1px solid var(--c-border)', fontSize: 13.5, resize: 'vertical' }}
        />
      </Field>

      {/* BD import metadata — read-only, shown only when contact came from BigDough */}
      {hasBdMeta && !isEdit && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '9px 12px', marginBottom: 16, fontSize: 12.5, color: 'var(--c-text-2)' }}>
          <span style={{ fontWeight: 600, color: '#166534', marginRight: 8 }}>BigDough data</span>
          {bdMeta.bd_mandate && <span style={{ marginRight: 12 }}>Strategy: {bdMeta.bd_mandate}</span>}
          {bdMeta.bd_aum_personal != null && <span style={{ marginRight: 12 }}>Personal AUM: {formatAum(bdMeta.bd_aum_personal)}</span>}
          {bdMeta.bd_contact_id && <span style={{ color: 'var(--c-text-3)' }}>ID: {bdMeta.bd_contact_id}</span>}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 8 }}>
        {isEdit && (
          <button
            className="btn btn--sm"
            style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', cursor: 'pointer' }}
            onClick={handleDelete}
            disabled={saving || deleting}
          >
            {deleting ? 'Deleting…' : '🗑 Delete Contact'}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost" onClick={onCancel} disabled={saving || deleting}>Cancel</button>
        <button className="btn btn--primary" onClick={handleSave} disabled={saving || deleting}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Contact'}
        </button>
      </div>
    </div>
  );
}
